const loadableWarning =
  'Warning: %s uses the legacy contextTypes API which is no longer supported and will be removed in the next major release. Use React.createContext() with static contextType instead.\n\nLearn more about this warning here: https://reactjs.org/link/legacy-context%s';
const emotionWarning =
  'The pseudo class ":first-child" is potentially unsafe when doing server-side rendering. Try changing it to ":first-of-type".';

interface DevelopmentEnvironment {
  development: boolean;
  reactVersion: string;
  origin: string;
}

function isReviewedWarning(args: unknown[], origin: string): boolean {
  // This browser-only sandbox has no SSR. Keep the exact existing advisory.
  if (args.length === 1 && args[0] === emotionWarning) {
    return true;
  }
  if (
    args.length !== 3 ||
    args[0] !== loadableWarning ||
    args[1] !== 'LoadableComponent' ||
    typeof args[2] !== 'string'
  ) {
    return false;
  }
  const [prefix, ...frames] = args[2].split('\n    at ');
  if (
    prefix !== '' ||
    !frames.length ||
    frames.some((frame) => /[\r\n]/.test(frame))
  ) {
    return false;
  }
  const firstFrame = frames[0].match(
    /^LoadableComponent \((https?:\/\/[^\s]+):(\d+):(\d+)\)$/
  );
  if (!firstFrame) {
    return false;
  }
  try {
    const source = new URL(firstFrame[1]);
    return source.origin === origin && source.pathname.endsWith('.js');
  } catch {
    return false;
  }
}

export function installDevelopmentWarningRouting(
  environment: DevelopmentEnvironment,
  target: Pick<Console, 'error' | 'warn'> = console
): () => void {
  if (!environment.development || environment.reactVersion !== '18.3.1') {
    return () => void 0;
  }
  const originalError = target.error;
  const originalWarn = target.warn;
  // Parcel treats console.error advisories as runtime crashes and reparses the
  // large editor source map for every frame, starving lazy asset requests.
  // Preserve the full reviewed messages as warnings; all other errors retain
  // Parcel's normal overlay, source-map handling and console output.
  const routeError = (...args: unknown[]) => {
    if (isReviewedWarning(args, environment.origin)) {
      originalWarn.apply(target, args);
    } else {
      originalError.apply(target, args);
    }
  };
  target.error = routeError;
  return () => {
    if (target.error === routeError) {
      target.error = originalError;
    }
  };
}
